"use client";

import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Flame, Loader2 } from "lucide-react";
import { apiClient } from "@/lib/utils/api-client";
import {
  HeatmapExplorer,
  type HeatmapFetcher,
} from "@/components/heatmap/heatmap-explorer";

export default function HeatmapViewPage() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // 마운트 시 기존 쿠키로 인증돼 있는지 확인
  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient("/api/heatmap/view");
        setAuthed(res.ok);
      } catch {
        setAuthed(false);
      }
    })();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await apiClient("/api/heatmap/view", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        setAuthed(true);
      } else {
        const j = await res.json().catch(() => ({}));
        setError(j.error || "비밀번호가 올바르지 않습니다.");
      }
    } catch {
      setError("요청 처리 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const fetcher: HeatmapFetcher = useCallback(async (params) => {
    const qs = params.path
      ? `?path=${encodeURIComponent(params.path)}&device=${params.device ?? "desktop"}${
          params.all ? "&all=1" : `&width=${params.width ?? 0}`
        }`
      : "";
    const res = await apiClient(`/api/heatmap/view${qs}`);
    if (res.status === 401) {
      setAuthed(false);
      throw new Error("unauthorized");
    }
    const json = await res.json();
    return json.data ?? json;
  }, []);

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-muted/20">
        <Card className="p-6 w-full max-w-sm space-y-4">
          <div className="flex items-center gap-2 font-bold text-lg">
            <Flame className="h-5 w-5 text-orange-500" />
            웹사이트 히트맵
          </div>
          <p className="text-sm text-muted-foreground">접속 비밀번호를 입력하세요.</p>
          <form onSubmit={submit} className="space-y-3">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호"
              autoFocus
            />
            {error && <p className="text-xs text-red-600">{error}</p>}
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "보기"}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Flame className="h-6 w-6 text-orange-500" />
          웹사이트 히트맵
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          클릭·마우스이동·스크롤 데이터를 페이지 위에 시각화합니다. 방문자 화면 폭(width)별로 분리되며
          &lsquo;전체 종합&rsquo;으로 합쳐 볼 수 있습니다.
        </p>
      </div>
      <HeatmapExplorer fetcher={fetcher} />
    </div>
  );
}
